import { type RefObject } from 'react';
import { type AutoSuggestOptionGroup } from '@pkgs/components/hooks/useAutoSuggestOptionGroups';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { Autocomplete } from '@base-ui/react/autocomplete';

import { LocationNotFound } from './LocationNotFound';
import { type AutoSuggestOption } from './AutoComplete';

import styles from './AutoCompletePopover.module.css';

type AutoCompletePopoverProps = {
  renderItem: (item: AutoSuggestOption) => JSX.Element;
  onItemSelect: (item: AutoSuggestOption) => void;
  isLocationNotFound?: boolean;
  portalContainer: RefObject<HTMLDivElement | null>;
};

export const AutoCompletePopover = ({ renderItem, onItemSelect, isLocationNotFound, portalContainer }: AutoCompletePopoverProps) => {
  const { t } = useAppContext();

  return (
    <Autocomplete.Portal container={portalContainer}>
      <Autocomplete.Positioner
        side="bottom"
        align="start"
        sideOffset={2}
        collisionAvoidance={{ fallbackAxisSide: 'none' }}
        className={styles.positioner}
      >
        <Autocomplete.Popup data-testid="search-components_autocomplete_popover" className={styles.popover}>
          <LocationNotFound isLocationNotFound={isLocationNotFound} />
          <Autocomplete.List>
            {(group: AutoSuggestOptionGroup) => (
              <Autocomplete.Group
                key={group.value}
                items={group.items}
                className={group.displayHeader ? styles.listBoxSectionWithHeader : undefined}
              >
                {group.displayHeader ? (
                  <Autocomplete.GroupLabel>
                    <div className={styles.headingWrapper}>
                      <Copy as="h5" size="s" text={t('search.searchbar.recentSearch.title')} />
                    </div>
                  </Autocomplete.GroupLabel>
                ) : null}
                <Autocomplete.Collection>
                  {(item: AutoSuggestOption) => (
                    <Autocomplete.Item key={item.id} value={item} className={styles.listItem} onClick={() => onItemSelect(item)}>
                      {renderItem(item)}
                    </Autocomplete.Item>
                  )}
                </Autocomplete.Collection>
              </Autocomplete.Group>
            )}
          </Autocomplete.List>
        </Autocomplete.Popup>
      </Autocomplete.Positioner>
    </Autocomplete.Portal>
  );
};
